import { isEmpty } from 'underscore';
import { useMemo } from 'react';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import { defaultUserGovernanceConfig } from './user-governance-config';
import { PreferenceState } from './preference-state';
import { PreferenceService } from './preference-service';
import type {
  IPreferenceService, IUserGovernanceConfig, IFeaturePreferenceHookProps, IUseShouldShowProps,
  IFeaturePreference, IFeaturePreferenceService,
} from './types';

// Overwrite the default config to treat close button as dismiss button in phase1
export const getTeachingBubbleUserGovernanceConfig = (): IUserGovernanceConfig => {
  const teachingBubbleUserGovernanceConfig = cloneDeep(defaultUserGovernanceConfig);

  teachingBubbleUserGovernanceConfig[PreferenceState.INIT].stateMachine.onClose = PreferenceState.FIRST_DISMISS;
  teachingBubbleUserGovernanceConfig[PreferenceState.FIRST_DISMISS].stateMachine.onClose = PreferenceState.SECOND_DISMISS;
  teachingBubbleUserGovernanceConfig[PreferenceState.SECOND_DISMISS].stateMachine.onClose = PreferenceState.THIRD_DISMISS;
  teachingBubbleUserGovernanceConfig[PreferenceState.THIRD_DISMISS].stateMachine.onClose = PreferenceState.STOP_SHOWN;

  return teachingBubbleUserGovernanceConfig;
};

export function useFeaturePreferenceService(preferenceService: IPreferenceService<IFeaturePreference>): IFeaturePreferenceService {
  const featurePreferenceService = useMemo(() => new PreferenceService({
    preferenceKey: 'user-governance-preference',
    preferenceService,
  }), [preferenceService]);

  return featurePreferenceService;
}

export function useFeaturePreference({
  featurePreferenceService,
  userGovernanceConfig,
  featureName,
}: IFeaturePreferenceHookProps): Partial<IFeaturePreference> {
  const featurePreference = useMemo(() => {
    if (!featureName || !userGovernanceConfig) {
      return {};
    }

    let preference = featurePreferenceService.getPreference(featureName);

    if (isEmpty(preference)) {
      preference = {
        state: PreferenceState.INIT,
        nextShownDate: featurePreferenceService.getDaysAfter(userGovernanceConfig[PreferenceState.INIT].cooldownDays),
        expirationDate: userGovernanceConfig.expirationDate,
      };
      featurePreferenceService.setPreference(featureName, preference);
    }

    return preference;
  }, [featurePreferenceService, userGovernanceConfig, featureName]);

  return featurePreference;
}

export const useShouldShow = ({
  featureName,
  preferenceService,
  userGovernanceConfig,
}: IUseShouldShowProps) => {
  const featurePreferenceService = useFeaturePreferenceService(preferenceService);

  const featurePreference = useFeaturePreference({
    featurePreferenceService,
    userGovernanceConfig,
    featureName,
  });
  const show = useMemo(() => {
    if (window.location.href.includes('isPopupCoolDownLogicDisabledForTestEnabled=true')) {
      // This is only used for test, where the popup needs to be triggered daily
      return true;
    }
    if (window.location.href.includes('isPreventPopupForTestEnabled=true')) {
      // This is only used for test, where the popup needs not to be triggered to block the other tests
      return false;
    }

    if (!featureName || !userGovernanceConfig) {
      return false;
    } else if (userGovernanceConfig.expirationDate
      && userGovernanceConfig.expirationDate.getTime() < Date.now()) {
      // If the expiration date is set and it is in the past, do not show the component
      return false;
    } else if (featurePreference.state === PreferenceState.STOP_SHOWN) {
      // if the state is stop shown, do not show the component
      return false;
    } else if (new Date(featurePreference.nextShownDate as Date) > new Date()) {
      // if the next shown date is in the future, do not show the component
      return false;
    }

    return true;
  }, [featureName, featurePreference, userGovernanceConfig]);

  return show;
};
